import { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ATCFeed {
  stream_url: string;
  airport_code: string;
  airport_name: string;
  feed_type?: string;
}

const MAX_STREAMS_PER_AIRPORT = 2;
const MAX_CANDIDATE_AIRPORTS = 8;
const STREAM_CONNECT_TIMEOUT_MS = 18000;

const FEED_PRIORITY: Record<string, number> = {
  tower: 0,
  ground: 1,
  departure: 2,
  approach: 3,
  delivery: 4,
  center: 5,
  atis: 6,
  mixed: 7,
};

const WEAK_STREAM_PATTERNS = [/apron/i, /backup/i, /coordinator/i, /emer/i, /murp/i, /ops/i, /ramp/i, /company/i];

const isPreferredFeedType = (feedType?: string) => ['tower', 'ground', 'departure', 'approach'].includes(feedType ?? '');

const isStrongStreamCandidate = (feed: ATCFeed) => !WEAK_STREAM_PATTERNS.some((pattern) => pattern.test(feed.stream_url));

const shuffleFeeds = <T,>(items: T[]) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const prioritizeFeeds = (feeds: ATCFeed[]) =>
  [...feeds].sort((a, b) => {
    const left = FEED_PRIORITY[a.feed_type ?? 'mixed'] ?? FEED_PRIORITY.mixed;
    const right = FEED_PRIORITY[b.feed_type ?? 'mixed'] ?? FEED_PRIORITY.mixed;
    return left - right;
  });

const uniqueFeedsByUrl = (feeds: ATCFeed[]) => {
  const seen = new Set<string>();
  return feeds.filter((feed) => {
    if (seen.has(feed.stream_url)) return false;
    seen.add(feed.stream_url);
    return true;
  });
};

const buildCandidateFeeds = (feeds: ATCFeed[]) => {
  const strongFeeds = feeds.filter(isStrongStreamCandidate);
  const preferredFeeds = (strongFeeds.length > 0 ? strongFeeds : feeds).filter((feed) => isPreferredFeedType(feed.feed_type));
  const candidateSource = preferredFeeds.length > 0 ? preferredFeeds : (strongFeeds.length > 0 ? strongFeeds : feeds);
  const grouped = new Map<string, ATCFeed[]>();

  for (const feed of candidateSource) {
    const bucket = grouped.get(feed.airport_code) ?? [];
    bucket.push(feed);
    grouped.set(feed.airport_code, bucket);
  }

  const airportCandidates = shuffleFeeds(Array.from(grouped.entries())).slice(0, MAX_CANDIDATE_AIRPORTS);
  return airportCandidates.flatMap(([, airportFeeds]) => prioritizeFeeds(airportFeeds).slice(0, MAX_STREAMS_PER_AIRPORT));
};

interface AudioContextType {
  // Playback state
  isPlaying: boolean;
  
  // Volume controls
  musicVolume: number;
  soundVolume: number;
  
  // ATC stream
  atcStreamUrl: string | null;
  isAtcActive: boolean;
  isAtcLoading: boolean;
  currentAirportCode: string | null;
  /** Airport currently being attempted (for "Connecting to X..." UI). */
  pendingAirportCode: string | null;
  /** Loading phase: 'searching' = scanning DB, 'connecting' = attempting a specific feed. */
  atcLoadingPhase: 'idle' | 'searching' | 'connecting';
  /** True if last auto-search exhausted candidates without success. */
  atcUnavailable: boolean;
  
  // Controls
  playPause: () => void;
  setMusicVolume: (volume: number) => void;
  setSoundVolume: (volume: number) => void;
  setAtcStream: (url: string | null, airportCode?: string) => Promise<void>;
  toggleAtc: () => void;
  /** Manually retry auto-connect after failure. */
  retryAtcConnection: () => Promise<void>;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within AudioProvider");
  }
  return context;
};

interface AudioProviderProps {
  children: ReactNode;
}

export const AudioProvider = ({ children }: AudioProviderProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicVolume, setMusicVolumeState] = useState(70);
  const [soundVolume, setSoundVolumeState] = useState(50);
  const [atcStreamUrl, setAtcStreamUrl] = useState<string | null>(null);
  const [isAtcActive, setIsAtcActive] = useState(false);
  const [isAtcLoading, setIsAtcLoading] = useState(false);
  const [currentAirportCode, setCurrentAirportCode] = useState<string | null>(null);
  const [pendingAirportCode, setPendingAirportCode] = useState<string | null>(null);
  const [atcLoadingPhase, setAtcLoadingPhase] = useState<'idle' | 'searching' | 'connecting'>('idle');
  const [atcUnavailable, setAtcUnavailable] = useState(false);
  const [hasAutoConnected, setHasAutoConnected] = useState(false);
  
  const atcAudioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const isAtcActiveRef = useRef(false);
  const currentAirportCodeRef = useRef<string | null>(null);
  const failedUrlsRef = useRef<Set<string>>(new Set());
  const isConnectingRef = useRef(false);
  
  // Keep refs in sync
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  
  useEffect(() => {
    isAtcActiveRef.current = isAtcActive;
  }, [isAtcActive]);
  
  useEffect(() => {
    currentAirportCodeRef.current = currentAirportCode;
  }, [currentAirportCode]);

  // Find and connect to a working ATC feed
  const findWorkingAtcFeed = useCallback(async (excludeAirport?: string): Promise<boolean> => {
    if (isConnectingRef.current) {
      console.log('Already connecting to ATC, skipping...');
      return false;
    }
    
    isConnectingRef.current = true;
    setIsAtcLoading(true);
    setAtcLoadingPhase('searching');
    setAtcUnavailable(false);
    setPendingAirportCode(null);
    
    try {
      console.log('Finding working ATC feed...', excludeAirport ? `excluding ${excludeAirport}` : '');
      
      let query = supabase
        .from('atc_feeds')
        .select('stream_url, airport_code, airport_name, feed_type')
        .eq('is_active', true)
        .order('airport_code', { ascending: true })
        .limit(120);
      
      if (excludeAirport) {
        query = query.neq('airport_code', excludeAirport);
      }
      
      const { data: feeds } = await query;
      
      if (!feeds || feeds.length === 0) {
        console.error('No ATC feeds available');
        setIsAtcLoading(false);
        setAtcLoadingPhase('idle');
        setAtcUnavailable(true);
        isConnectingRef.current = false;
        return false;
      }
      
      const availableFeeds = feeds.filter(f => !failedUrlsRef.current.has(f.stream_url));
      const candidatePool = availableFeeds.length > 0 ? availableFeeds : (failedUrlsRef.current.clear(), feeds);
      const limitedCandidates = buildCandidateFeeds(candidatePool);

      for (const feed of limitedCandidates) {
        console.log(`Trying ${feed.airport_code} (${feed.airport_name})...`);
        setAtcLoadingPhase('connecting');
        setPendingAirportCode(feed.airport_code);
        const success = await tryConnectToFeed(feed.stream_url, feed.airport_code, feed.airport_name);
        if (success) {
          return true;
        }
        failedUrlsRef.current.add(feed.stream_url);
      }
      
      console.error('Could not connect to any ATC feed');
      setIsAtcLoading(false);
      setAtcLoadingPhase('idle');
      setPendingAirportCode(null);
      setAtcUnavailable(true);
      isConnectingRef.current = false;
      toast({
        title: "Couldn't reach ATC",
        description: "All nearby airports failed to connect. Tap retry or pick another airport.",
        variant: "destructive",
      });
      return false;
    } catch (error) {
      console.error('Error finding ATC feed:', error);
      setIsAtcLoading(false);
      setAtcLoadingPhase('idle');
      setPendingAirportCode(null);
      setAtcUnavailable(true);
      isConnectingRef.current = false;
      return false;
    }
  }, []);

  // Try to connect to a specific feed with timeout
  const tryConnectToFeed = useCallback(async (url: string, airportCode: string, airportName: string): Promise<boolean> => {
    return new Promise((resolve) => {
      // Clean up existing audio
      if (atcAudioRef.current) {
        atcAudioRef.current.pause();
        atcAudioRef.current.src = '';
        atcAudioRef.current = null;
      }
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const proxiedUrl = `${supabaseUrl}/functions/v1/atc-proxy?url=${encodeURIComponent(url)}`;
      // Direct only works if URL is HTTPS (mixed-content blocks http:// streams on HTTPS pages)
      const canTryDirect = url.startsWith('https://') && url.includes('liveatc.net');
      
      const attemptConnection = (useProxy: boolean): Promise<boolean> => {
        return new Promise((resolveAttempt) => {
          const targetUrl = useProxy ? proxiedUrl : url;
          console.log(`Trying ${airportCode} ${useProxy ? 'via proxy' : 'direct'}...`);

          const audio = new Audio(targetUrl);
          // No crossOrigin: HTML5 <audio> can play streams without CORS headers,
          // and LiveATC does not return Access-Control-Allow-Origin. Setting
          // crossOrigin='anonymous' would make every direct attempt fail.
          audio.preload = "auto";
          const calculatedVolume = soundVolume / 100;
          if (isFinite(calculatedVolume)) {
            audio.volume = calculatedVolume;
          }

          let resolved = false;
          const timeout = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              console.log(`Timeout connecting to ${airportCode} ${useProxy ? 'via proxy' : 'direct'}`);
              audio.pause();
              audio.src = '';
              resolveAttempt(false);
            }
          }, STREAM_CONNECT_TIMEOUT_MS);

          const cleanup = () => clearTimeout(timeout);

          const handleReady = () => {
            if (!resolved) {
              resolved = true;
              cleanup();
              console.log(`Successfully connected to ${airportCode} ${useProxy ? 'via proxy' : 'direct'}`);

              atcAudioRef.current = audio;
              setAtcStreamUrl(url);
              setCurrentAirportCode(airportCode);
              setPendingAirportCode(null);
              setAtcLoadingPhase('idle');
              setIsAtcLoading(false);
              setAtcUnavailable(false);
              isConnectingRef.current = false;

              setupStreamErrorHandling(audio, airportCode);

              toast({
                title: `Connected to ${airportCode}`,
                description: airportName,
              });

              resolveAttempt(true);
            }
          };

          audio.addEventListener('loadedmetadata', handleReady, { once: true });
          audio.addEventListener('loadeddata', handleReady, { once: true });
          audio.addEventListener('canplay', handleReady, { once: true });
          audio.addEventListener('playing', handleReady, { once: true });
          audio.addEventListener('progress', () => {
            if (!resolved && (audio.readyState >= 2 || audio.buffered.length > 0)) {
              handleReady();
            }
          });

          audio.addEventListener('error', () => {
            if (!resolved) {
              resolved = true;
              cleanup();
              console.log(`Failed to connect to ${airportCode} ${useProxy ? 'via proxy' : 'direct'}`);
              audio.src = '';
              resolveAttempt(false);
            }
          });

          audio.load();
          audio.play().catch((error) => {
            console.log(`Playback start deferred for ${airportCode} ${useProxy ? 'via proxy' : 'direct'}`, error);
          });
        });
      };

      // Direct-first: LiveATC currently blocks our edge-runtime IP, so the
      // proxy returns 403. The browser's IP usually still works directly.
      // Fall back to the proxy only if direct playback fails.
      const tryConnection = async () => {
        if (canTryDirect) {
          const directSuccess = await attemptConnection(false);
          if (directSuccess) {
            resolve(true);
            return;
          }
        }
        const proxySuccess = await attemptConnection(true);
        resolve(proxySuccess);
      };

      tryConnection();
    });
  }, [soundVolume]);

  // Set up error handling for an active stream
  const setupStreamErrorHandling = useCallback((audio: HTMLAudioElement, airportCode: string) => {
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 2;
    
    const handleStreamFailure = async () => {
      console.log(`Stream failure for ${airportCode}, finding new feed...`);
      failedUrlsRef.current.add(audio.src);
      
      if (isPlayingRef.current) {
        const success = await findWorkingAtcFeed(airportCode);
        if (success && atcAudioRef.current) {
          setIsAtcActive(true);
          atcAudioRef.current.play().catch(console.error);
        }
      }
    };
    
    audio.addEventListener('error', () => {
      console.error(`ATC stream error for ${airportCode}`);
      handleStreamFailure();
    });
    
    audio.addEventListener('stalled', () => {
      console.warn(`ATC stream stalled for ${airportCode}`);
      reconnectAttempts++;
      if (reconnectAttempts >= maxReconnectAttempts) {
        handleStreamFailure();
      }
    });
    
    audio.addEventListener('ended', () => {
      console.log(`ATC stream ended for ${airportCode}`);
      handleStreamFailure();
    });
    
    audio.addEventListener('playing', () => {
      reconnectAttempts = 0;
      setIsAtcActive(true);
      setIsAtcLoading(false);
    });
    
    audio.addEventListener('waiting', () => {
      setIsAtcLoading(true);
    });
    
    audio.addEventListener('canplay', () => {
      setIsAtcLoading(false);
    });
  }, [findWorkingAtcFeed]);

  // Auto-connect to ATC on page load
  useEffect(() => {
    if (hasAutoConnected) return;
    
    const autoConnectATC = async () => {
      console.log('Auto-connecting to ATC on page load...');
      setHasAutoConnected(true);
      await findWorkingAtcFeed();
    };
    
    const timer = setTimeout(autoConnectATC, 1000);
    return () => clearTimeout(timer);
  }, [hasAutoConnected, findWorkingAtcFeed]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isPlayingRef.current && atcAudioRef.current) {
        if (isAtcActiveRef.current && atcAudioRef.current.paused) {
          atcAudioRef.current.play().catch(console.error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Update ATC volume
  useEffect(() => {
    if (atcAudioRef.current) {
      const calculatedVolume = soundVolume / 100;
      if (isFinite(calculatedVolume)) {
        atcAudioRef.current.volume = calculatedVolume;
      }
    }
  }, [soundVolume]);

  const playPause = useCallback(async () => {
    console.log('PlayPause clicked, current state:', isPlaying);
    
    if (isPlaying) {
      atcAudioRef.current?.pause();
      setIsPlaying(false);
      setIsAtcActive(false);
    } else {
      setIsPlaying(true);
      
      // If no ATC connected or not ready, find one
      if (!atcAudioRef.current || !atcStreamUrl) {
        console.log('No ATC connected, finding working feed...');
        const success = await findWorkingAtcFeed();
        if (success && atcAudioRef.current) {
          setIsAtcActive(true);
          atcAudioRef.current.play().catch(async (e) => {
            console.error('Error playing ATC after connect:', e);
            // Try another feed
            const retrySuccess = await findWorkingAtcFeed(currentAirportCodeRef.current || undefined);
            if (retrySuccess && atcAudioRef.current) {
              atcAudioRef.current.play().catch(console.error);
            }
          });
        }
      } else {
        // Try to play existing connection
        setIsAtcActive(true);
        try {
          await atcAudioRef.current.play();
        } catch (e) {
          console.error('Error playing existing ATC:', e);
          // Current stream failed, find a new one
          const success = await findWorkingAtcFeed(currentAirportCodeRef.current || undefined);
          if (success && atcAudioRef.current) {
            setIsAtcActive(true);
            atcAudioRef.current.play().catch(console.error);
          }
        }
      }
    }
  }, [isPlaying, atcStreamUrl, findWorkingAtcFeed]);

  const setAtcStream = useCallback(async (url: string | null, airportCode?: string) => {
    console.log('Setting ATC stream:', url, 'Airport:', airportCode);
    
    if (!url) {
      if (atcAudioRef.current) {
        atcAudioRef.current.pause();
        atcAudioRef.current = null;
      }
      setAtcStreamUrl(null);
      setIsAtcActive(false);
      setCurrentAirportCode(null);
      setPendingAirportCode(null);
      setAtcLoadingPhase('idle');
      return;
    }
    
      if (airportCode) {
      // Show "Connecting to <airport>..." immediately
      setPendingAirportCode(airportCode);
      setAtcLoadingPhase('connecting');
      setIsAtcLoading(true);
      setAtcUnavailable(false);
      isConnectingRef.current = true;

      const manualCandidates: ATCFeed[] = [{
        stream_url: url,
        airport_code: airportCode,
        airport_name: airportCode,
      }];

      const { data: airportFeeds } = await supabase
        .from('atc_feeds')
        .select('stream_url, airport_code, airport_name, feed_type')
        .eq('airport_code', airportCode)
        .eq('is_active', true)
        .limit(8);

      if (airportFeeds?.length) {
        manualCandidates.push(...airportFeeds.filter(isStrongStreamCandidate));
      }

      const fallbackCandidates = uniqueFeedsByUrl(prioritizeFeeds(manualCandidates.filter((candidate) => candidate.stream_url !== url)))
        .slice(0, Math.max(0, MAX_STREAMS_PER_AIRPORT - 1));
      const rankedCandidates = uniqueFeedsByUrl([manualCandidates[0], ...fallbackCandidates]);

      let success = false;
      for (const candidate of rankedCandidates) {
        success = await tryConnectToFeed(candidate.stream_url, airportCode, candidate.airport_name);
        if (success) break;
        failedUrlsRef.current.add(candidate.stream_url);
      }

      if (!success) {
        // User explicitly selected this feed - don't auto-failover, show error
        toast({
          title: `Could not connect to ${airportCode}`,
          description: `We tried ${rankedCandidates.length} ${airportCode} stream${rankedCandidates.length === 1 ? '' : 's'} and none responded. Try another airport.`,
          variant: "destructive",
        });
        setIsAtcLoading(false);
        setAtcLoadingPhase('idle');
        setPendingAirportCode(null);
        isConnectingRef.current = false;
      }
    }
  }, [tryConnectToFeed]);

  const toggleAtc = useCallback(async () => {
    console.log('Toggle ATC, current state:', isAtcActive);
    
    if (isAtcActive) {
      atcAudioRef.current?.pause();
      setIsAtcActive(false);
    } else {
      if (!atcAudioRef.current) {
        const success = await findWorkingAtcFeed();
        if (success && atcAudioRef.current) {
          if (!isPlaying) setIsPlaying(true);
          setIsAtcActive(true);
          atcAudioRef.current.play().catch(console.error);
        }
      } else {
        if (!isPlaying) setIsPlaying(true);
        setIsAtcActive(true);
        try {
          await atcAudioRef.current.play();
        } catch (e) {
          console.error('Error playing ATC:', e);
          const success = await findWorkingAtcFeed(currentAirportCodeRef.current || undefined);
          if (success && atcAudioRef.current) {
            atcAudioRef.current.play().catch(console.error);
          }
        }
      }
    }
  }, [isAtcActive, isPlaying, findWorkingAtcFeed]);

  const retryAtcConnection = useCallback(async () => {
    failedUrlsRef.current.clear();
    const success = await findWorkingAtcFeed();
    if (success && atcAudioRef.current && isPlayingRef.current) {
      setIsAtcActive(true);
      atcAudioRef.current.play().catch(console.error);
    }
  }, [findWorkingAtcFeed]);

  const value: AudioContextType = {
    isPlaying,
    musicVolume,
    soundVolume,
    atcStreamUrl,
    isAtcActive,
    isAtcLoading,
    currentAirportCode,
    pendingAirportCode,
    atcLoadingPhase,
    atcUnavailable,
    playPause,
    setMusicVolume: setMusicVolumeState,
    setSoundVolume: setSoundVolumeState,
    setAtcStream,
    toggleAtc,
    retryAtcConnection,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
